import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DateRangeContext = createContext();

// Helper: formatear fecha a YYYY-MM-DD
const fmt = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
};

// Helper: calcular rango desde un preset
export const getPresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today), label: 'Hoy', preset };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: fmt(y), to: fmt(y), label: 'Ayer', preset };
    }
    case '7d': {
      const d = new Date(today); d.setDate(d.getDate() - 6);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 7 días', preset };
    }
    case '15d': {
      const d = new Date(today); d.setDate(d.getDate() - 14);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 15 días', preset };
    }
    case '30d':
    default: {
      const d = new Date(today); d.setDate(d.getDate() - 29);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 30 días', preset: '30d' };
    }
    case '90d': {
      const d = new Date(today); d.setDate(d.getDate() - 89);
      return { from: fmt(d), to: fmt(today), label: 'Últimos 90 días', preset };
    }
    case 'this_month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: fmt(d), to: fmt(today), label: 'Este mes', preset };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(start), to: fmt(end), label: 'Mes anterior', preset };
    }
  }
};

const DEFAULT_RANGE = getPresetRange('30d');
const STORAGE_KEY = 'adpulse_daterange';

export function DateRangeProvider({ children }) {
  const [range, setRange] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Si era preset, recalcular (las fechas absolutas caducan)
        if (parsed?.preset && parsed.preset !== 'custom') {
          return getPresetRange(parsed.preset);
        }
        // Si era custom, usar las fechas guardadas tal cual
        if (parsed?.from && parsed?.to) return parsed;
      }
    } catch {}
    return DEFAULT_RANGE;
  });

  // Guardar cada cambio
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
    } catch {}
  }, [range]);

  // Setter por preset
  const setPreset = useCallback((preset) => {
    setRange(getPresetRange(preset));
  }, []);

  // Setter custom (desde calendario)
  const setCustomRange = useCallback((from, to) => {
    setRange({
      from,
      to,
      label: `${from} → ${to}`,
      preset: 'custom',
    });
  }, []);

  return (
    <DateRangeContext.Provider value={{ range, setRange, setPreset, setCustomRange }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error('useDateRange must be used within DateRangeProvider');
  }
  return ctx;
}
